import { gql, useLazyQuery } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import { useNavigate } from 'react-router';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_SERIES_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import type {
  CommonEssenceRewardedQuery,
  CommonEssenceRewardedQueryVariables,
} from './__generated__/useOnCommonEssenceClick.graphql.ts';

const CommonEssenceRewardedQuery = gql`
  query CommonEssenceRewardedQuery($sport: Sport!) {
    market {
      id
      recommendedCardPack(sport: $sport) {
        slug
      }
    }
  }
` as TypedDocumentNode<
  CommonEssenceRewardedQuery,
  CommonEssenceRewardedQueryVariables
>;

export const useOnCommonEssenceClick = (isFirstEssenceReward: boolean) => {
  const navigate = useNavigate();
  const openDialog = useOpenDialog();
  const { sport, generateSportContextPath } = useSportContext();
  const [getData] = useLazyQuery(CommonEssenceRewardedQuery);

  return async () => {
    const seriesMarketPath = generateSportContextPath(ANY_SPORT_SERIES_MARKET);

    if (!isFirstEssenceReward) {
      navigate(seriesMarketPath);
      return;
    }

    const { data } = await getData({ variables: { sport } });
    const recommendedPackSlug = data?.market.recommendedCardPack?.slug;

    if (recommendedPackSlug) {
      openDialog(DialogKey.packDetails, recommendedPackSlug, {
        basePath: seriesMarketPath,
      });
      return;
    }

    navigate(seriesMarketPath);
  };
};
